import { Controller } from '@hotwired/stimulus'
import { i18n } from 'config/translations'

export default class extends Controller {
  static targets = [
    'noAdults',
    'noNurseryAboard',
    'noChildrenNursery',
    'noChildrenNoNursery',
    'noChildrenSchool',
    'summaryText',
    'breakdownNoAdult',
    'breakdownNoNurseryAboard',
    'breakdownNoChildrenNursery',
    'breakdownNoChildrenNoNursery',
    'breakdownNoChildrenSchool',
  ]

  static values = {
    maxChildren: String,
    maxAdults: String,
    roomMaxCount: String,
    serviceType: String,
  }

  connect() {
    const initial = this.initialCounts()
    this.noAdultsTarget.textContent = initial.adults
    this.noNurseryAboardTarget.textContent = initial.nurseryAboard
    this.noChildrenNurseryTarget.textContent = initial.childrenNursery
    this.noChildrenNoNurseryTarget.textContent = '0'
    this.noChildrenSchoolTarget.textContent = '0'
    this.breakdownNoAdultTarget.textContent = initial.adults
    this.breakdownNoNurseryAboardTarget.textContent = initial.nurseryAboard
    this.breakdownNoChildrenNurseryTarget.textContent = initial.childrenNursery
    this.breakdownNoChildrenNoNurseryTarget.textContent = '0'
    this.breakdownNoChildrenSchoolTarget.textContent = '0'
    this.summaryTextTarget.textContent = i18n.t('top.component.people')
  }

  // [issue#2291] 既定は「大人2・こども1」だが、そのままだと大人上限1の宿などで初期表示から
  // 定員超過になり、加算側のガード（addAdult 等）をすり抜けて予約に進めてしまう。
  // 部屋定員に収まるところまで初期値を引き下げる。大人は最低1名を維持する。
  initialCounts() {
    const isPreschool = this.serviceTypeValue.includes('保育園留学')
    const isElementary = this.serviceTypeValue.includes('小学生留学')
    let adults = 2
    let children = (isPreschool ? 1 : 0) + (isElementary ? 1 : 0)

    const maxAdults = this.capacityValue('maxAdultsValue')
    const maxChildren = this.capacityValue('maxChildrenValue')
    const roomMax = this.capacityValue('roomMaxCountValue')

    if (maxAdults !== null) adults = Math.min(adults, maxAdults)
    if (maxChildren !== null) children = Math.min(children, maxChildren)
    // 合計上限は「大人1名分を先に確保してからこども」「残りを大人」の順に割り当てる。
    // こどもを先に確定させると roomMax=1 の宿で大人1+こども1=2 となり、大人の下限1と
    // 衝突して初期値が定員超過のままになる。
    if (roomMax !== null) {
      children = Math.min(children, Math.max(0, roomMax - 1))
      adults = Math.min(adults, roomMax - children)
    }
    adults = Math.max(1, adults)

    return {
      adults: String(adults),
      // こども内訳は「保育園留学」「小学生留学」の順に定員まで割り当てる
      nurseryAboard: String(isPreschool && children > 0 ? 1 : 0),
      childrenNursery: String(isElementary && children > (isPreschool ? 1 : 0) ? 1 : 0),
    }
  }

  // 定員値（data 属性は文字列）。未設定・0・数値でない場合は「上限なし」として null を返す。
  // parseInt('') は NaN で比較が常に false になり、ガードが黙って無効化されるための防御。
  capacityValue(name) {
    const parsed = parseInt(this[name])
    return Number.isNaN(parsed) || parsed <= 0 ? null : parsed
  }

  getNoAdult() {
    return this.noAdultsTarget.textContent
  }

  getNoNurseryAboard() {
    return this.noNurseryAboardTarget.textContent
  }

  getNoChildrenNursery() {
    return this.noChildrenNurseryTarget.textContent
  }

  getNoChildrenNoNursery() {
    return this.noChildrenNoNurseryTarget.textContent
  }

  getNoChildrenSchool() {
    return this.noChildrenSchoolTarget.textContent
  }

  sumPeople() {
    return (
      parseInt(this.getNoAdult()) +
      parseInt(this.getNoNurseryAboard()) +
      parseInt(this.getNoChildrenNursery()) +
      parseInt(this.getNoChildrenNoNursery()) +
      parseInt(this.getNoChildrenSchool())
    )
  }

  sumChildren() {
    return (
      parseInt(this.getNoNurseryAboard()) +
      parseInt(this.getNoChildrenNursery()) +
      parseInt(this.getNoChildrenNoNursery()) +
      parseInt(this.getNoChildrenSchool())
    )
  }

  // [issue#2291] 大人をもう1名追加できるか（部屋の合計上限・大人上限）
  canAddAdult() {
    const roomMax = this.capacityValue('roomMaxCountValue')
    const maxAdults = this.capacityValue('maxAdultsValue')
    if (roomMax !== null && this.sumPeople() >= roomMax) return false
    return maxAdults === null || parseInt(this.getNoAdult()) < maxAdults
  }

  // [issue#2291] こどもをもう1名追加できるか（部屋の合計上限・こども上限）
  canAddChild() {
    const roomMax = this.capacityValue('roomMaxCountValue')
    const maxChildren = this.capacityValue('maxChildrenValue')
    if (maxChildren !== null && this.sumChildren() >= maxChildren) return false
    return roomMax === null || this.sumPeople() < roomMax
  }

  addAdult(event) {
    const noAdults = parseInt(this.getNoAdult())
    if (!this.canAddAdult()) {
      return
    }
    this.noAdultsTarget.textContent = String(noAdults + 1)
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  reduceAdult(event) {
    const noAdults = parseInt(this.getNoAdult())
    if (noAdults <= 1) {
      return
    }
    this.noAdultsTarget.textContent = String(noAdults - 1)
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  addChildNursery(event) {
    if (!this.canAddChild()) {
      return
    }
    this.noChildrenNurseryTarget.textContent = String(parseInt(this.getNoChildrenNursery()) + 1)
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  reduceChildNursery(event) {
    const noChildrenNursery = parseInt(this.getNoChildrenNursery())
    if (noChildrenNursery <= 1) {
      return
    }
    this.noChildrenNurseryTarget.textContent = String(noChildrenNursery - 1)
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  addNurseryAboard(event) {
    if (!this.canAddChild()) {
      return
    }
    this.noNurseryAboardTarget.textContent = String(parseInt(this.getNoNurseryAboard()) + 1)
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  reduceNurseryAboard(event) {
    const noNurseryAboard = parseInt(this.getNoNurseryAboard())
    if (noNurseryAboard <= 1) {
      return
    }
    this.noNurseryAboardTarget.textContent = String(noNurseryAboard - 1)
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  addChildNoNursery(event) {
    if (!this.canAddChild()) {
      return
    }
    this.noChildrenNoNurseryTarget.textContent = String(parseInt(this.getNoChildrenNoNursery()) + 1)
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  reduceChildNoNursery(event) {
    const noChildrenNoNursery = parseInt(this.getNoChildrenNoNursery())
    if (noChildrenNoNursery <= 0) {
      return
    }
    this.noChildrenNoNurseryTarget.textContent = String(noChildrenNoNursery - 1)
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  addChildSchool(event) {
    if (!this.canAddChild()) {
      return
    }
    this.noChildrenSchoolTarget.textContent = String(parseInt(this.getNoChildrenSchool()) + 1)
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  reduceChildSchool(event) {
    const noChildrenSchool = parseInt(this.getNoChildrenSchool())
    if (noChildrenSchool <= 0) {
      return
    }
    this.noChildrenSchoolTarget.textContent = String(noChildrenSchool - 1)
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  save(event) {
    const totalNoChidren =
      parseInt(this.getNoNurseryAboard()) +
      parseInt(this.getNoChildrenNursery()) +
      parseInt(this.getNoChildrenNoNursery()) +
      parseInt(this.getNoChildrenSchool())
    this.summaryTextTarget.textContent =
      i18n.t('reservation.summary.adult', { count: parseInt(this.getNoAdult()) }) +
      i18n.t('reservation.summary.child', { count: totalNoChidren })
    const totalPriceController = this.application.getControllerForElementAndIdentifier(this.element, 'total-price')
    if (totalPriceController) {
      totalPriceController.setPeople(
        parseInt(this.getNoAdult()),
        parseInt(this.getNoNurseryAboard()),
        parseInt(this.getNoChildrenNursery()),
        parseInt(this.getNoChildrenNoNursery()),
        parseInt(this.getNoChildrenSchool()),
      )
      totalPriceController.calculate()
      this.breakdownNoAdultTarget.textContent = this.getNoAdult()
      this.breakdownNoNurseryAboardTarget.textContent = this.getNoNurseryAboard()
      this.breakdownNoChildrenNurseryTarget.textContent = this.getNoChildrenNursery()
      this.breakdownNoChildrenNoNurseryTarget.textContent = this.getNoChildrenNoNursery()
      this.breakdownNoChildrenSchoolTarget.textContent = this.getNoChildrenSchool()
    }
  }

  mobileCheck = function () {
    let check = false
    ;(function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a,
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
          a.substr(0, 4),
        )
      ) {
        check = true
      }
    })(navigator.userAgent || navigator.vendor || window.opera)
    return check
  }

  clear() {
    // [issue#2291] リセット後も定員内に収める（connect と同じ初期値を使う）
    const initial = this.initialCounts()
    this.summaryTextTarget.textContent = i18n.t('top.component.people')
    this.noAdultsTarget.textContent = initial.adults
    this.noNurseryAboardTarget.textContent = initial.nurseryAboard
    this.noChildrenNurseryTarget.textContent = initial.childrenNursery
    this.noChildrenNoNurseryTarget.textContent = 0
    this.noChildrenSchoolTarget.textContent = 0
    this.save()
  }
}
